import React, { useMemo, useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MessageCircle, Zap, Check, X } from 'lucide-react';
import { playSuccess, playWrong } from '../../utils/sounds';
import { Celebration } from '../Celebration';
import { shuffle } from '../../utils/shuffle';
interface FramingOption {
  label: string;
  description: string;
  effectiveness: 'high' | 'medium' | 'low';
  insight: string;
}
const FRAMINGS: Record<string, FramingOption[]> = {
  Nephrologist: [{
    label: 'Safety-first reassurance',
    description: '"The data shows this is safe even in your most fragile patients."',
    effectiveness: 'high',
    insight: 'Directly addresses their #1 concern. Leading with safety unlocks everything else.'
  }, {
    label: 'Evidence-based urgency',
    description: '"Every month of delay means measurable, irreversible kidney function loss."',
    effectiveness: 'high',
    insight: 'Combines their respect for evidence with the cost of their cautious approach.'
  }, {
    label: 'Proactive positioning',
    description: '"Be the specialist who catches it early, not the one managing end-stage."',
    effectiveness: 'medium',
    insight: 'Appeals to their frustration with late referrals, but can feel confrontational.'
  }, {
    label: 'Peer momentum',
    description: '"Leading nephrologists are already integrating this into their protocols."',
    effectiveness: 'medium',
    insight: 'Moderately effective — they respect peers but trust data more.'
  }, {
    label: 'Simplicity pitch',
    description: '"It\'s easy to add to your existing regimen with minimal monitoring."',
    effectiveness: 'low',
    insight: "Misses the mark — they don't need simplicity, they need safety confidence."
  }],
  Cardiologist: [{
    label: 'Outcome prevention',
    description: '"This reduces MACE events by X% — that\'s Y fewer patients hospitalised."',
    effectiveness: 'high',
    insight: 'Speaks their language. Hard endpoints and absolute numbers drive action.'
  }, {
    label: 'Dual organ protection',
    description: '"One treatment decision that protects both heart and kidney."',
    effectiveness: 'high',
    insight: 'Aligns with their recognition of cardio-renal interplay. Efficient and proactive.'
  }, {
    label: 'Aggressive early action',
    description: '"The earlier you add this, the bigger the outcome benefit."',
    effectiveness: 'medium',
    insight: 'Aligns with their proactive identity but needs to be backed by specific data.'
  }, {
    label: 'Guideline compliance',
    description: '"Guidelines now recommend this as part of standard care."',
    effectiveness: 'medium',
    insight: 'Helpful but cardiologists often act ahead of guidelines — not their primary motivator.'
  }, {
    label: 'Safety reassurance',
    description: '"Don\'t worry about the initial eGFR dip — it stabilises."',
    effectiveness: 'low',
    insight: "They're already comfortable with this trade-off. Feels patronising."
  }],
  Endocrinologist: [{
    label: 'Completing the protection',
    description: '"You\'ve addressed metabolic risk — now complete the kidney protection."',
    effectiveness: 'high',
    insight: "Acknowledges what they're already doing and positions the next step as natural extension."
  }, {
    label: 'Biomarker-triggered action',
    description: '"When UACR reaches X, the evidence says it\'s time to add this."',
    effectiveness: 'high',
    insight: 'Gives them a clear, data-driven trigger — removes the ambiguity they dislike.'
  }, {
    label: 'Preventative framing',
    description: '"This is prevention, not escalation — it fits your approach."',
    effectiveness: 'medium',
    insight: 'Good reframe but needs to be backed by specific preventative outcome data.'
  }, {
    label: 'Peer prescribing data',
    description: '"Other endocrinologists are prescribing this without referring out."',
    effectiveness: 'medium',
    insight: 'Addresses their hesitancy to "overstep" but data matters more than peers.'
  }, {
    label: 'Aggressive multi-modal push',
    description: '"Add everything early — don\'t wait for progression."',
    effectiveness: 'low',
    insight: 'Clashes with their cautious, stepwise approach. Feels like overstepping.'
  }],
  PCP: [{
    label: 'Guideline-backed confidence',
    description: '"The guidelines are clear — this is now standard of care for these patients."',
    effectiveness: 'high',
    insight: 'Removes the confidence barrier. They want permission to act.'
  }, {
    label: 'Peer normalisation',
    description: '"PCPs across the country are already doing this successfully."',
    effectiveness: 'high',
    insight: 'Social proof is uniquely powerful for PCPs — it validates their capability.'
  }, {
    label: 'Simple patient identification',
    description: '"If your patient has X and Y, they\'re a candidate. It\'s that simple."',
    effectiveness: 'high',
    insight: 'Reduces decision complexity — the #1 thing PCPs need.'
  }, {
    label: 'Prevention narrative',
    description: '"Treat now to prevent the specialist referral later."',
    effectiveness: 'medium',
    insight: 'Aligns with their holistic approach but needs to feel actionable, not abstract.'
  }, {
    label: 'Complex outcome data',
    description: '"The FIDELIO-DKD trial showed a 18% relative risk reduction in..."',
    effectiveness: 'low',
    insight: 'Too detailed. PCPs need clear, simple messages — not trial deep-dives.'
  }]
};
export function MotivationFraming({
  state,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const framings = useMemo(() => shuffle(FRAMINGS[role]), [role]);
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [celebration, setCelebration] = useState<'success' | 'wrong' | null>(null);
  const handleSelect = (label: string) => {
    if (revealed) return;
    setSelected(label);
  };
  const handleReveal = () => {
    setRevealed(true);
    const selectedFraming = framings.find(f => f.label === selected);
    if (selectedFraming?.effectiveness === 'high') {
      playSuccess();
      setCelebration('success');
    } else {
      playWrong();
      setCelebration('wrong');
    }
    setTimeout(() => setCelebration(null), 2000);
  };
  const effectivenessConfig = {
    high: {
      label: 'High Impact',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      dot: 'bg-green-500'
    },
    medium: {
      label: 'Moderate Impact',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      dot: 'bg-amber-500'
    },
    low: {
      label: 'Low Impact',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      dot: 'bg-red-500'
    }
  };
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-262">
      <Celebration type={celebration} color={color} data-id="element-263" />
      <div className="max-w-4xl w-full mx-auto" data-id="element-264">
        <div className="text-center mb-8" data-id="element-265">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-4" style={{
          backgroundColor: `${color}15`,
          color
        }} data-id="element-266">
            <Zap size={28} data-id="element-267" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-3" data-id="element-268">
            How should you frame it for {role}s?
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-269">
            You know the features — now pick the <strong data-id="element-270">framing</strong> that
            would be most effective. Which motivation approach would you lead
            with?
          </p>
        </div>

        {!revealed ? <>
            <div className="space-y-3 mb-8" data-id="element-271">
              {framings.map(framing => {
            const isSelected = selected === framing.label;
            return <motion.button key={framing.label} whileHover={{
              scale: 1.005
            }} whileTap={{
              scale: 0.995
            }} onClick={() => handleSelect(framing.label)} className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 ${isSelected ? 'bg-white shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'}`} style={{
              borderColor: isSelected ? color : undefined
            }} data-id="element-272">
                    <div className="flex items-start" data-id="element-273">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 mt-0.5 flex-shrink-0 transition-colors ${isSelected ? 'border-transparent' : 'border-gray-300'}`} style={{
                  backgroundColor: isSelected ? color : 'transparent'
                }} data-id="element-274">
                        {isSelected && <div className="w-2 h-2 bg-white rounded-full" data-id="element-275" />}
                      </div>
                      <div data-id="element-276">
                        <h4 className={`font-bold mb-1 ${isSelected ? 'text-dark' : 'text-gray-800'}`} data-id="element-277">
                          {framing.label}
                        </h4>
                        <p className="text-gray-500 italic leading-relaxed" data-id="element-278">
                          {framing.description}
                        </p>
                      </div>
                    </div>
                  </motion.button>;
          })}
            </div>

            <div className="flex justify-center h-16" data-id="element-279">
              {selected ? <motion.button initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} onClick={handleReveal} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-full transition-colors shadow-lg" style={{
            backgroundColor: color
          }} data-id="element-280">
                  Reveal What Works
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-281" />
                </motion.button> : <p className="text-gray-400 text-sm self-center" data-id="element-282">
                  Select the framing your group thinks is most effective
                </p>}
            </div>
          </> : <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-283">
            {/* Result banner */}
            {(() => {
          const selectedFraming = framings.find(f => f.label === selected);
          const eff = selectedFraming?.effectiveness || 'low';
          const config = effectivenessConfig[eff];
          return <div className={`${config.bg} ${config.border} border rounded-2xl p-5 mb-8 text-center`} data-id="element-284">
                  <p className={`text-lg font-bold ${config.text}`} data-id="element-285">
                    Your group chose a{' '}
                    <span className="lowercase" data-id="element-286">{config.label}</span> framing
                    {eff === 'high' ? ' — great instinct!' : eff === 'medium' ? ' — on the right track.' : ' — see why below.'}
                  </p>
                </div>;
        })()}

            {/* All framings with effectiveness revealed */}
            <div className="space-y-4 mb-8" data-id="element-287">
              {framings.map((framing, i) => {
            const config = effectivenessConfig[framing.effectiveness];
            const wasSelected = selected === framing.label;
            return <motion.div key={framing.label} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.1
            }} className={`rounded-2xl border-2 overflow-hidden ${wasSelected ? 'shadow-md' : ''}`} style={{
              borderColor: wasSelected ? color : '#e5e7eb'
            }} data-id="element-288">
                    <div className={`p-5 ${wasSelected ? 'bg-gray-50' : 'bg-white'}`} data-id="element-289">
                      <div className="flex items-start justify-between mb-2" data-id="element-290">
                        <div className="flex items-start" data-id="element-291">
                          {wasSelected && <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mr-2 mt-0.5" style={{
                      backgroundColor: `${color}15`,
                      color
                    }} data-id="element-292">
                              Your pick
                            </span>}
                          <h4 className="font-bold text-dark" data-id="element-293">
                            {framing.label}
                          </h4>
                        </div>
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${config.bg} ${config.text} flex items-center`} data-id="element-294">
                          <span className={`w-2 h-2 rounded-full ${config.dot} mr-1.5`} data-id="element-295"></span>
                          {config.label}
                        </span>
                      </div>
                      <p className="text-gray-500 italic mb-3" data-id="element-296">
                        {framing.description}
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-297">
                        <strong data-id="element-298">Why:</strong> {framing.insight}
                      </p>
                    </div>
                  </motion.div>;
          })}
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8 flex items-start" data-id="element-299">
              <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-300" />
              <div data-id="element-301">
                <h4 className="text-blue-900 font-bold mb-2" data-id="element-302">
                  Discuss with your group:
                </h4>
                <ul className="text-blue-800 space-y-1.5" data-id="element-303">
                  <li data-id="element-304">
                    How would you combine the top-rated framing with the
                    features you identified earlier?
                  </li>
                  <li data-id="element-305">
                    Can you craft a 30-second opening that uses the right
                    motivation for this specialty?
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center pb-8" data-id="element-306">
              <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-307">
                Build Your Strategy Card
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-308" />
              </button>
            </div>
          </motion.div>}
      </div>
    </div>;
}